/* eslint-disable react/no-multi-comp */
import React, { useRef, useContext, useMemo } from 'react';

import { Row, Col } from '@app/ui';
import LineChart, {
    LineChartOptionsProp,
} from '../../../components/charts/LineChart';
import useCrowdFlow, {
    MAX_CROWD_FLOW_GAMES,
} from '../../../components/hooks/inVenue/useCrowdFlow';
import { InVenueContext } from '../../InVenue.context';
import { decimal, shortDate } from '../../../utils/formatters';
import usePrevious from '../../../components/hooks/usePrevious';

const CrowdFlow = () => {
    const chartRef = useRef(null);
    const {
        client,
        current: { rightsholder, season, gameDates, sections },
        selectedDate,
        isLoadingFilters,
    } = useContext(InVenueContext);

    const params = {
        client,
        rightsholder,
        season,
        gameDates: selectedDate ? [selectedDate] : gameDates,
        sectionIds: sections.map(section => section.id),
    };

    const previousParams = usePrevious(params);
    const shouldUpdate =
        !previousParams ||
        JSON.stringify(params) !== JSON.stringify(previousParams);

    const { data, isLoading } = useCrowdFlow(params);

    const isLoadingChart = isLoading || isLoadingFilters;

    const gamesCount = data?.datasets?.length || 0;

    const lineChart = useMemo(() => {
        const options: LineChartOptionsProp = {
            xType: 'category',
            yType: 'decimal',
            title: '',
            config: {
                plugins: {
                    datalabels: {
                        display: gamesCount <= 1,
                        align: 'end',
                        anchor: 'start',
                        formatter: decimal,
                    },
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            align: 'middle',
                            fontSize: 14,
                            padding: 16,
                        },
                    },
                    title: {
                        text: selectedDate ? shortDate(selectedDate) : '',
                        color: '#7a7a7a',
                        font: { size: 11 },
                    },
                },
                scales: {
                    x: {
                        title: {
                            display: true,
                            text: 'Time from Start',
                            align: 'center',
                            font: { size: 14 },
                        },
                        grid: {
                            drawBorder: true,
                            lineWidth: 1,
                        },
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Attendance',
                            align: 'center',
                            font: { size: 14 },
                        },
                        grid: {
                            display: false,
                            drawBorder: true,
                            lineWidth: 1,
                        },
                    },
                },
                animation: {
                    duration: 0,
                },
            },
        };

        return (
            <LineChart
                ref={chartRef}
                data={data}
                options={options}
                isLoading={isLoadingChart}
            />
        );
    }, [selectedDate, isLoadingChart, gamesCount, shouldUpdate]);

    const title =
        gamesCount >= MAX_CROWD_FLOW_GAMES
            ? `Crowd Flow (Last ${MAX_CROWD_FLOW_GAMES} Games)`
            : 'Crowd Flow';

    return (
        <Row gutter={16} className="invenue-flex-container">
            <Col md={24}>
                <p className="-m-b-1">{title}</p>
            </Col>
            <Col md={24}>
                <div className="stadium-attendance-card stadium-attendance-chart-container -m-b-3">
                    <div className="stadium-attendance-chart-container stadium-attendance-chart-container-crowd ">
                        {lineChart}
                    </div>
                </div>
            </Col>
        </Row>
    );
};

export default CrowdFlow;
